import {
  HOME_MOVES_MOVE_OUT_COMPLETE,
  MOVE_OUT_CONFIRM_METER_READ_SUBMIT,
} from '@/src/constants/analytics-subjects';
import { ROUTE_METER_READINGS } from '@/src/constants/routes';
import {
  Card,
  CTAButton,
  Heading1,
  Heading2,
  Heading4,
  Li,
  Margin,
  P,
  Stack,
  Strong,
  Ul,
} from '@ovotech/element-v4';
import { AnalyticsClick, useAnalytics } from '@ovotech/ui-tools';
import React, { useEffect, useMemo } from 'react';
import { Step } from '../Step/Step';
import styles from './MoveOutSupplyingHomeScreen.module.css';

export const MoveOutSupplyingHomeScreen = ({
  moveOutAddress,
  moveOutDate,
}: {
  moveOutAddress: string;
  moveOutDate: string;
}) => {
  const movingDateText = useMemo(() => {
    const date = new Date(moveOutDate);

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }, [moveOutDate]);

  const { dispatch } = useAnalytics();

  useEffect(() => {
    dispatch({
      type: 'view',
      name: HOME_MOVES_MOVE_OUT_COMPLETE,
      properties: {
        status: 'takingOvo',
      },
    });
  }, [dispatch]);

  return (
    <Stack spaceBetween={[8, 7, 4]}>
      <Card>
        <Stack spaceBetween={3}>
          <Margin bottom={10}>
            <Heading1>We’re setting up your supply at your new home</Heading1>
          </Margin>

          <Heading4 className={styles.heading}>Your new address</Heading4>

          <Heading4>{moveOutAddress}</Heading4>

          <P>
            We’ll start powering your new home on{' '}
            <Strong>{movingDateText}</Strong>. Don’t worry if your account isn’t
            updated yet, it can take a while to process a move, so keep checking
            back.
          </P>
        </Stack>
      </Card>

      <Card>
        <Heading2 className={styles.title}>What’s next?</Heading2>
        <Stack spaceBetween={[6, 10, 10]}>
          <Step iconText="1">
            <P className={styles.listTitle}>Send your final meter reading</P>

            <Margin bottom={4}>
              <Ul showBullets={false} className={styles.list}>
                <Li>
                  If you have a smart meter, we’ll take a reading automatically.
                </Li>
                <Li>If you don’t, please send a reading on your moving day.</Li>
                <Li>If we don’t have a reading, we’ll estimate your bill.</Li>
                <Li>
                  If you need to submit a reading after your moving day, please
                  contact us.
                </Li>
              </Ul>
            </Margin>

            <AnalyticsClick
              name={MOVE_OUT_CONFIRM_METER_READ_SUBMIT}
              properties={{
                moveOutDate,
              }}
            >
              <CTAButton
                onClick={() => {
                  window.open(ROUTE_METER_READINGS, '_blank');
                }}
              >
                Send meter reading
              </CTAButton>
            </AnalyticsClick>
          </Step>
          <Step iconText="2">
            <P className={styles.listTitle}>Cancel your other OVO services</P>
            <P>
              Be sure to end your contract for other OVO services, like Boiler
              Cover, that are linked to your old home.
            </P>
          </Step>
          <Step iconText="3">
            <P className={styles.listTitle}>Your final bill</P>
            <P>
              We'll send your final bill within six weeks of your moving out.
              Please{' '}
              <span className={styles.underline}>
                don't cancel your Direct Debit
              </span>
              , as we'll use it to pay your balance, then we'll cancel it for
              you.
            </P>
          </Step>
        </Stack>
      </Card>
    </Stack>
  );
};
